import { useState } from 'react';
import { Modal, Form, Input, Button, Space, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, MailOutlined, LinkOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';

const { Text } = Typography;

const copyLink = url => {
    navigator.clipboard?.writeText(url)
        .then(() => message.success('Invite link copied'))
        .catch(() => message.warning('Could not copy automatically — select the link and copy it'));
};

/**
 * Lightweight alternative to CreateEntityModal for the common case of inviting
 * one or more people: just name + email per row. Underneath, each row does
 * exactly what Add Entity does for a person entity (POST /api/entities with
 * entity_type: 'person') and then immediately mints a claim link the same way
 * the superadmin "Send claim email" action on an unclaimed person's page does
 * (POST /api/entities/{id}/claim-link) — surfacing a copyable link per person
 * so an invite still works when the entity has no email or SMTP isn't
 * configured on this node.
 */
function InvitePeopleModal({ open, onCancel, onSuccess }) {
    const [form] = Form.useForm();
    const api = useApi();
    const { user, userPersonEntity } = useAuth();
    const [inviting, setInviting] = useState(false);
    const [results, setResults] = useState(null); // [{ name, email, claim_url, email_sent, error }]

    const resetState = () => {
        form.resetFields();
        setResults(null);
        setInviting(false);
    };

    const handleClose = () => {
        resetState();
        onCancel?.();
    };

    const handleInvite = async (people, sendEmail) => {
        setInviting(true);
        const rowResults = [];
        for (const p of people) {
            try {
                const entity = await api.post('/api/entities', {
                    name: p.name,
                    email: p.email,
                    entity_type: 'person',
                    is_listed: false,
                    ...(userPersonEntity?.id ? { created_by: userPersonEntity.id } : {}),
                });
                try {
                    const link = await api.post(`/api/entities/${entity.id}/claim-link`, undefined, {
                        params: { user_id: user?.id, send_email: sendEmail },
                    });
                    rowResults.push({ name: p.name, email: p.email, claim_url: link.claim_url, email_sent: link.email_sent });
                } catch (err) {
                    // Entity was still created — just couldn't mint/send the link.
                    rowResults.push({ name: p.name, email: p.email, error: err.message || 'Invited, but could not generate a link' });
                }
            } catch (err) {
                rowResults.push({ name: p.name, email: p.email, error: err.message || 'Could not create invite' });
            }
        }
        setResults(rowResults);
        setInviting(false);
        onSuccess?.();
    };

    const handleCreateLinks = async () => {
        try {
            const { people } = await form.validateFields();
            await handleInvite(people, false);
        } catch {
            // form validation error — antd already surfaces it inline
        }
    };

    return (
        <Modal title="Invite People" open={open} onCancel={handleClose} footer={null} width={560}>
            {!results ? (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={({ people }) => handleInvite(people, true)}
                    initialValues={{ people: [{ name: '', email: '' }] }}
                >
                    <Form.List name="people">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...rest }) => (
                                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'name']}
                                            rules={[{ required: true, message: 'Name required' }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder="Name" style={{ width: 200 }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'email']}
                                            rules={[
                                                { required: true, message: 'Email required' },
                                                { type: 'email', message: 'Enter a valid email' },
                                            ]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder="Email" style={{ width: 220 }} />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                            />
                                        )}
                                    </Space>
                                ))}
                                <Button
                                    type="dashed"
                                    onClick={() => add({ name: '', email: '' })}
                                    icon={<PlusOutlined />}
                                    style={{ marginBottom: 16 }}
                                >
                                    Add another person
                                </Button>
                            </>
                        )}
                    </Form.List>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleClose}>Cancel</Button>
                            <Button onClick={handleCreateLinks} loading={inviting} icon={<LinkOutlined />}>
                                Create invite links
                            </Button>
                            <Button type="primary" htmlType="submit" loading={inviting} icon={<MailOutlined />}>
                                Send invites
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {results.map((r, i) => (
                        <div key={i}>
                            <Space size={6}>
                                <Text strong>{r.name}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>
                            </Space>
                            {r.error ? (
                                <div><Text type="danger" style={{ fontSize: 12 }}>{r.error}</Text></div>
                            ) : (
                                <>
                                    <Input
                                        readOnly
                                        value={r.claim_url}
                                        onFocus={e => e.target.select()}
                                        style={{ marginTop: 4 }}
                                        addonAfter={
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                style={{ padding: 0 }}
                                                onClick={() => copyLink(r.claim_url)}
                                            />
                                        }
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {r.email_sent ? 'Invite emailed' : 'Email not sent — share the link manually'}
                                    </Text>
                                </>
                            )}
                        </div>
                    ))}
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={resetState}>Invite more</Button>
                        <Button type="primary" onClick={handleClose}>Done</Button>
                    </Space>
                </Space>
            )}
        </Modal>
    );
}

export default InvitePeopleModal;
